import { PlanCapabilitiesService } from '@shared/services/plan-capabilities.service';
import { menuDoPlano } from './menu-do-plano';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuConfig } from '@core/_config/menu.config';
import { AuthService, TenancyService } from '@core/services/common';
import { GetCurrentSessionResult } from '@core/model/common';
import { ItemDeMenu } from './menu.model';
import { ButtonComponent } from '@shared/components/buttons/button/button.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import { NgxPermissionsModule } from 'ngx-permissions';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatBadge } from '@angular/material/badge';
import { UserMenuComponent } from '../header/user-menu.component';
import { CentralSupportBadgeService } from 'src/app/views/pages/central/support/services/central-support-badge.service';
import { ConfiguracaoAtencaoService } from '@shared/services/configuracao-atencao.service';
import { menuComRecebimentos } from './recebimentos-menu';

/**
 * O menu lateral do shell próprio — substitui `kt-aside-left` + `kt-brand`.
 *
 * O que ele deixou de precisar, e é o ponto da onda C3: `KTOffcanvas`,
 * `KTUtil`, a diretiva `ktMenu` e o `perfect-scrollbar`. A gaveta é CSS, a
 * rolagem é `overflow` nativo com `cdkScrollable`, e o estado de aberto/fechado
 * é do componente — nenhum script global, nenhuma classe escrita no `<body>`.
 *
 * **De onde vem o menu.** Direto de `MenuConfig`, que é código do produto. O
 * shell antigo passava por `MenuConfigService` + `MenuAsideService`, dois
 * repasses do tema cuja ordem de construção era uma armadilha: o
 * `MenuAsideService` lê a configuração no PRÓPRIO construtor, então só
 * funcionava porque o `kt-base` chamava `loadConfigs` antes de o menu existir.
 * Ler a fonte tira a armadilha e não muda um byte do que é exibido.
 */
@Component({
  selector: 'mg-aside',
  templateUrl: './aside.component.html',
  styleUrls: ['./aside.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    ButtonComponent,
    CdkScrollable,
    NgTemplateOutlet,
    NgxPermissionsModule,
    MatTooltip,
    MatIcon,
    MatListModule,
    MatDividerModule,
    MatExpansionModule,
    MatBadge,
    UserMenuComponent,
  ],
})
export class AsideComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly plano = inject(PlanCapabilitiesService);
  private readonly tenancy = inject(TenancyService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthService);
  private readonly centralSupportBadge = inject(CentralSupportBadgeService);
  private readonly configuracaoAtencao = inject(ConfiguracaoAtencaoService);

  /** Modo ícone no desktop. Quem guarda o estado é a casca. */
  @Input() recolhido = false;

  @Output() alternarRecolhido = new EventEmitter<void>();

  /** Pedido de fechamento da gaveta — navegou, então a gaveta sai da frente. */
  @Output() fechar = new EventEmitter<void>();

  itens: ItemDeMenu[] = [];
  urlAtual = '';
  chamadosEmAberto = 0;

  /** Sessão para o bloco de perfil do rodapé — mesmo caminho que `user-menu.component.ts` já usa. */
  usuario: GetCurrentSessionResult = null;

  private readonly expandidos = new Set<ItemDeMenu>();
  private readonly inscricoes = new Subscription();
  private menuBase: ItemDeMenu[] = [];

  ngOnInit(): void {
    const menus = new MenuConfig().configs.aside;
    const ehTenant = this.tenancy.isSubdomain;
    this.menuBase = ehTenant ? menuDoPlano(menus.subdomain_items as ItemDeMenu[], this.plano.modulos) : menus.items as ItemDeMenu[];
    this.itens = menuComRecebimentos(this.menuBase, null);

    this.urlAtual = this.semQueryNemFragmento(this.router.url);
    this.abrirGruposDaRota();
    this.carregarBadgeDeSuporte(ehTenant);
    this.carregarMenuDeRecebimentos(ehTenant);

    this.inscricoes.add(
      this.router.events.pipe(filter(evento => evento instanceof NavigationEnd)).subscribe(() => {
        const novaUrl = this.semQueryNemFragmento(this.router.url);
        // A abertura manual vale até sair da página; a rota de destino
        // mantém abertos somente os grupos necessários para localizar o item.
        if (novaUrl !== this.urlAtual) this.expandidos.clear();
        this.urlAtual = novaUrl;
        this.abrirGruposDaRota();
        this.fechar.emit();
        this.cdr.markForCheck();
      })
    );

    this.inscricoes.add(
      this.authService.currentUserObserver.subscribe(sessao => {
        this.usuario = sessao;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.inscricoes.unsubscribe();
    this.configuracaoAtencao.limparRecebimentos();
  }

  /**
   * O item corresponde à rota atual?
   *
   * O critério é o mesmo do shell antigo — `indexOf(page) !== -1`, e não
   * igualdade: `/painel/financas/caixas/listar` marca "Caixas" e a tela de
   * edição de um caixa continua marcando o mesmo item.
   *
   * Achado da revisão (rodada 8, baixa): dois FILHOS do MESMO submenu podiam
   * casar ao mesmo tempo quando um `page` é prefixo do outro — o submenu de
   * Suporte pendurou 5 telas sob `/admin/chamados` (a página da própria
   * Fila), e `/admin/chamados/incidentes` casava tanto com "Fila de
   * atendimento" quanto com "Incidentes" (dois itens `[activated]` ao mesmo
   * tempo). Entre IRMÃOS do mesmo submenu, só o de `page` mais longo que
   * casa fica ativo; `/admin/chamados/<uuid>` (detalhe do chamado) continua
   * marcando só a Fila, porque nenhum irmão mais específico casa com ele.
   */
  estaAtivo(item: ItemDeMenu): boolean {
    if (item.submenu) {
      return item.submenu.some(filho => this.estaAtivo(filho));
    }
    if (!item.page || this.urlAtual.indexOf(item.page) === -1) {
      return false;
    }
    const pagina = item.page;
    return !this.irmaosDe(item).some(
      irmao => irmao !== item && !!irmao.page && irmao.page.length > pagina.length && this.urlAtual.indexOf(irmao.page) !== -1,
    );
  }

  /** O array (`itens` de raiz, ou `submenu` de um grupo) que contém `item` — para comparar só contra os irmãos dele. */
  private irmaosDe(item: ItemDeMenu, nos: ItemDeMenu[] = this.itens): ItemDeMenu[] {
    if (nos.includes(item)) {
      return nos;
    }
    for (const no of nos) {
      if (no.submenu) {
        const achado = this.irmaosDe(item, no.submenu);
        if (achado.length) {
          return achado;
        }
      }
    }
    return [];
  }

  estaAberto(item: ItemDeMenu): boolean {
    return this.expandidos.has(item);
  }

  /**
   * Sincroniza o Set com o painel: quem TOCA a UI do grupo é o
   * `mat-expansion-panel` (Task 9), e `(opened)`/`(closed)` devolvem a
   * verdade para cá — o Set continua sendo a única fonte, porque é ele quem
   * abre grupo por rota (`abrirGruposDaRota`) e quem decide se o submenu
   * oferece link (`@if` do template).
   */
  definirSubmenu(item: ItemDeMenu, aberto: boolean): void {
    if (aberto) {
      this.expandidos.add(item);
      return;
    }
    this.expandidos.delete(item);
  }

  /**
   * O cabeçalho do grupo (`mat-panel-title`) não desenha o filho quando o
   * painel está fechado — abre só pela rota (`abrirGruposDaRota`). Se a
   * contagem morasse só no filho, ela sumiria do menu em toda tela fora de
   * `/admin/chamados`. Por isso o grupo soma o badge dos filhos (visual §7:
   * "Se o menu não desenhar badge em filho, o badge fica também no pai"), e
   * a função vale tanto para a folha quanto para o cabeçalho do grupo.
   */
  badgeDoItem(item: ItemDeMenu): number {
    if (item.badge === 'support') {
      return this.chamadosEmAberto;
    }

    if (item.submenu) {
      return item.submenu.reduce((soma, filho) => soma + this.badgeDoItem(filho), 0);
    }

    return 0;
  }

  private carregarMenuDeRecebimentos(ehTenant: boolean): void {
    if (!ehTenant) {
      return;
    }

    this.inscricoes.add(
      this.configuracaoAtencao.recebimentosStatus$.subscribe(status => {
        this.itens = menuComRecebimentos(this.menuBase, status);
        this.abrirGruposDaRota();
        this.cdr.markForCheck();
      }),
    );
  }

  private carregarBadgeDeSuporte(ehTenant: boolean): void {
    if (ehTenant) {
      return;
    }

    this.inscricoes.add(
      this.centralSupportBadge.count$.subscribe(count => {
        this.chamadosEmAberto = count.minhas + count.triagem;
        this.cdr.markForCheck();
      })
    );
    this.centralSupportBadge.refresh();
  }

  private abrirGruposDaRota(itens = this.itens): void {
    for (const item of itens) {
      if (item.submenu && this.estaAtivo(item)) {
        this.expandidos.add(item);
        this.abrirGruposDaRota(item.submenu);
      }
    }
  }

  private semQueryNemFragmento(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
